import { getBlob, ref } from "firebase/storage";
import { auth, storage } from "../firebaseConfig";
import { LEARNING_CONTENT_API_BASE_URL, LearningContentApiError } from "./learningContentApi";

export const IMAGE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
export const PDF_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_CONCURRENT_UPLOADS = 2;

let fallbackMediaIdSequence = 0;
export const createLearningContentMediaId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  fallbackMediaIdSequence += 1;
  const suffix = fallbackMediaIdSequence.toString(16).padStart(12, "0").slice(-12);
  return `00000000-0000-4000-8000-${suffix}`;
};

export const validateLearningContentMediaFile = (file, kind) => {
  if (!file || typeof file.size !== "number" || file.size < 1) {
    throw new LearningContentApiError(kind === "image" ? "Select an image file." : "Select a PDF file.", 400);
  }
  if (kind === "image") {
    if (!IMAGE_MIME_TYPES.includes(file.type)) {
      throw new LearningContentApiError("Images must be JPEG, PNG, or WebP. SVG and GIF are not supported.", 415);
    }
    if (file.size > IMAGE_UPLOAD_MAX_BYTES) throw new LearningContentApiError("Images must be 5 MiB or smaller.", 413);
  } else if (kind === "resource") {
    if (file.type !== "application/pdf") throw new LearningContentApiError("Resources must be PDF files.", 415);
    if (file.size > PDF_UPLOAD_MAX_BYTES) throw new LearningContentApiError("PDF resources must be 10 MiB or smaller.", 413);
  } else {
    throw new LearningContentApiError("Unsupported learning-content media kind.", 400);
  }
  return file;
};

const authenticatedToken = async () => {
  if (!auth.currentUser) throw new LearningContentApiError("Please sign in again before managing learning-content media.", 401);
  return auth.currentUser.getIdToken();
};

const responseJson = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.ok !== true) {
    throw new LearningContentApiError(data.error || fallback, response.status);
  }
  return data;
};

export const uploadLearningContentMediaApi = async ({ quizId, blockId, kind, mediaId, file }) => {
  validateLearningContentMediaFile(file, kind);
  const token = await authenticatedToken();
  const form = new FormData();
  form.append("quizId", quizId);
  form.append("blockId", blockId);
  form.append("kind", kind);
  form.append("mediaId", mediaId);
  form.append("file", file, file.name);
  const response = await fetch(`${LEARNING_CONTENT_API_BASE_URL}/api/learning-content/media/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Learning-Content-Quiz-Id": quizId,
    },
    body: form,
  });
  return responseJson(response, "Media could not be uploaded. Please try again.");
};

export const deleteLearningContentMediaApi = async (quizId, mediaId) => {
  const token = await authenticatedToken();
  const response = await fetch(`${LEARNING_CONTENT_API_BASE_URL}/api/learning-content/media/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ quizId, mediaId }),
  });
  return responseJson(response, "Media cleanup could not be completed.");
};

export const loadLearningContentMediaBlobUrl = async (storagePath) => {
  const blob = await getBlob(ref(storage, storagePath));
  return URL.createObjectURL(blob);
};

export const mediaIdFromStoragePath = (storagePath) => {
  const fileName = String(storagePath || "").split("/").pop() || "";
  return fileName.replace(/\.(jpg|png|webp|pdf)$/i, "") || null;
};

export const uploadLearningContentMediaDrafts = async ({ quizId, blocks, mediaDrafts, onProgress }) => {
  let nextBlocks = blocks.map((block) => ({ ...block }));
  let nextDrafts = { ...mediaDrafts };
  const pendingUploads = [];

  for (let index = 0; index < nextBlocks.length; index += 1) {
    const block = nextBlocks[index];
    if (block.type !== "image" && block.type !== "resource") continue;
    const draft = nextDrafts[block.id];
    if (!draft?.file || draft.uploadedMedia) {
      if (draft?.uploadedMedia && block.storagePath !== draft.uploadedMedia.storagePath) {
        nextBlocks = nextBlocks.map((item, itemIndex) =>
          itemIndex === index
            ? { ...item, storagePath: draft.uploadedMedia.storagePath }
            : item
        );
      }
      continue;
    }
    pendingUploads.push({ index, block, draft });
  }

  let nextUploadIndex = 0;
  let firstFailure = null;
  let stopStartingUploads = false;

  const publishProgress = () => onProgress?.(nextBlocks, nextDrafts);
  const worker = async () => {
    while (!stopStartingUploads) {
      const uploadIndex = nextUploadIndex;
      nextUploadIndex += 1;
      if (uploadIndex >= pendingUploads.length) return;

      const { index, block, draft } = pendingUploads[uploadIndex];
      nextDrafts = {
        ...nextDrafts,
        [block.id]: { ...nextDrafts[block.id], status: "uploading", error: "" },
      };
      publishProgress();

      try {
        const response = await uploadLearningContentMediaApi({
          quizId,
          blockId: block.id,
          kind: block.type === "image" ? "image" : "resource",
          mediaId: draft.mediaId,
          file: draft.file,
        });
        nextBlocks = nextBlocks.map((item, itemIndex) =>
          itemIndex === index
            ? { ...item, storagePath: response.media.storagePath }
            : item
        );
        nextDrafts = {
          ...nextDrafts,
          [block.id]: {
            ...nextDrafts[block.id],
            file: null,
            status: "uploaded",
            error: "",
            uploadedMedia: response.media,
          },
        };
      } catch (error) {
        nextDrafts = {
          ...nextDrafts,
          [block.id]: {
            ...nextDrafts[block.id],
            status: "failed",
            error: error.message || "Upload failed.",
          },
        };
        if (!firstFailure) firstFailure = error;
        stopStartingUploads = true;
      }
      publishProgress();
    }
  };

  const workerCount = Math.min(MAX_CONCURRENT_UPLOADS, pendingUploads.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  if (firstFailure) {
    firstFailure.partialBlocks = nextBlocks;
    firstFailure.partialMediaDrafts = nextDrafts;
    throw firstFailure;
  }

  return { blocks: nextBlocks, mediaDrafts: nextDrafts };
};

export const cleanupUnreferencedUploadedMedia = async (quizId, blocks, mediaDrafts) => {
  const referenced = new Set(
    blocks.filter((block) => block.type === "image" || block.type === "resource").map((block) => block.storagePath).filter(Boolean)
  );
  const media = Object.values(mediaDrafts)
    .map((draft) => draft?.uploadedMedia)
    .filter((item) => item && !referenced.has(item.storagePath));
  return Promise.allSettled(media.map((item) => deleteLearningContentMediaApi(quizId, item.mediaId || mediaIdFromStoragePath(item.storagePath))));
};
